import { AlertTriangle, Hourglass, Loader2, RotateCcw, Send, TimerReset } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import type { RunHealth } from '~/features/workflow-tracking/types/workflow-runs.types';
import { cn } from '~/utils/cn';
import { formatDateTime, formatDurationMs } from '~/utils/date';

import type { BatchFunnel } from '../../types/batch-funnel.api.types';
import type { StageError } from '../../types/stage-errors.api.types';
import { FUNNEL_TONE_CHIP, verdictTone } from '../../utils/batch-funnel';
import { funnelStageOf } from '../../utils/stage-errors';

import { StageErrorDetails, StageErrorMeta } from './stage-error-details';

interface FunnelVerdictBannerProps {
  funnel: BatchFunnel;
  /** Run health from `links.health` — only read for the "stalled" hint. */
  health?: RunHealth | undefined;
  /** First current ERROR from `/errors` — only set while the batch is failed (§5.5).
   *  Adds the customer next step, ×N / timing and the root-cause Details. */
  bannerError?: StageError | null | undefined;
  onRetry: () => void;
  /** Failure points still loading — the retry dialog needs them. */
  retryLoading: boolean;
  /** Set when the server allows a retry but there's nothing to retry from
   *  (history past retention, no reset point, discovery failed) — keeps Retry
   *  disabled with this as its tooltip instead of opening an empty dialog. */
  retryUnavailableReason: string | null;
  /** Opens the reset confirm; only offered when `actions.resetPath` is set. */
  onReset: () => void;
  resetPending: boolean;
  /** Delivery-approval gate → the page's existing review flow. */
  onReviewDelivery: () => void;
}

/** Banner tint per verdict tone; ATTENTION is outlined rather than filled (§4.4). */
const BANNER_SURFACE = {
  info: 'border-info/30 bg-info-50',
  success: 'border-success/30 bg-success-50',
  warning: 'border-warning/30 bg-warning-50',
  error: 'border-error/30 bg-error-50',
  neutral: 'border-border bg-card',
} as const;

/**
 * Batch Details Funnel verdict banner — answers "what happened to my batch?":
 * state chip, server headline / reason / next step, ETA, batch-level failure,
 * the gate the batch is parked at, a stalled-run hint, and the Retry / Reset /
 * Review actions. Every sentence comes from the API; only chrome is translated.
 */
export function FunnelVerdictBanner({
  funnel,
  health,
  bannerError,
  onRetry,
  retryLoading,
  retryUnavailableReason,
  onReset,
  resetPending,
  onReviewDelivery,
}: FunnelVerdictBannerProps) {
  const { t } = useTranslation('catalog-operations');
  const { verdict, failure, gate, actions } = funnel;
  const state = verdict?.state ?? null;
  const tone = verdictTone(state);
  const isAttention = state === 'ATTENTION';
  const stageLabel = (key: string | null) =>
    funnel.stages?.find((s) => s.key === key)?.label ?? key ?? '—';
  // Stage-error contract §5.4: `retryable: false` means retrying can't succeed
  // without a change — hide Retry and lead with the next step instead. The
  // stage error's verdict wins over the funnel failure's when both exist.
  const errorRetryable = bannerError?.retryable ?? failure?.retryable ?? null;
  const hideRetry = errorRetryable === false;
  const failureStage = failure?.stage ?? (bannerError ? funnelStageOf(bannerError.stage) : null);
  const failureCode = failure?.errorCode ?? bannerError?.code ?? failure?.code ?? null;
  const failureMessage = bannerError?.message ?? failure?.message ?? null;
  // Skip the stage error's next step when it repeats the verdict's.
  const failureNextStep =
    bannerError?.nextStep && bannerError.nextStep !== verdict?.nextStep
      ? bannerError.nextStep
      : null;
  const stalled = health?.state === 'STALLED' && health.stalledForMillis !== null;
  // Retry needs both the server's go-ahead and a loaded point to retry from.
  const retryAllowed = actions?.retryAllowed === true;
  const retryBusy = retryAllowed && retryLoading;
  const canRetry = retryAllowed && !retryLoading && retryUnavailableReason === null;
  const retryTooltip = retryAllowed
    ? retryUnavailableReason
    : (actions?.retryBlockedReason ?? null);

  return (
    <section
      role="status"
      aria-live="polite"
      data-testid="batch-funnel-verdict-banner"
      className={cn(
        'space-y-3 rounded-xl border p-4',
        isAttention ? 'border-error bg-card' : BANNER_SURFACE[tone],
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              data-testid="batch-funnel-verdict-state-badge"
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold',
                isAttention
                  ? 'border-error text-error border bg-transparent'
                  : FUNNEL_TONE_CHIP[tone],
              )}
            >
              {state === 'RUNNING' && (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              )}
              {t(`batchFunnel.verdict.state.${state ?? 'UNKNOWN'}`, { defaultValue: state ?? '—' })}
            </span>
            {verdict?.etaSeconds != null && (
              <span
                className="text-muted-foreground text-xs"
                data-testid="batch-funnel-verdict-eta"
              >
                {t('batchFunnel.verdict.eta', {
                  duration: formatDurationMs(verdict.etaSeconds * 1000),
                })}
              </span>
            )}
          </div>
          <p
            className="text-foreground text-base font-semibold"
            data-testid="batch-funnel-verdict-headline"
          >
            {verdict?.headline ?? t('batchFunnel.verdict.noHeadline')}
          </p>
          {verdict?.reason && (
            <p className="text-foreground text-sm" data-testid="batch-funnel-verdict-reason">
              {verdict.reason}
            </p>
          )}
          {verdict?.nextStep && (
            <p
              className="text-muted-foreground text-sm"
              data-testid="batch-funnel-verdict-next-step"
            >
              <span className="text-foreground font-medium">{t('batchFunnel.whatToDo')}</span>{' '}
              {verdict.nextStep}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {gate?.type === 'DELIVERY_APPROVAL' && actions?.decisionRequired && (
            <Button
              size="sm"
              className="cursor-pointer"
              onClick={onReviewDelivery}
              data-testid="batch-funnel-verdict-review-delivery-button"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
              {t('batchFunnel.actions.reviewDelivery')}
            </Button>
          )}
          {actions?.resetPath && (
            <Button
              size="sm"
              variant="outline"
              className="cursor-pointer disabled:cursor-not-allowed"
              onClick={onReset}
              isLoading={resetPending}
              data-testid="batch-funnel-verdict-reset-button"
            >
              {!resetPending && <TimerReset className="h-4 w-4" aria-hidden="true" />}
              {t('batchFunnel.actions.reset')}
            </Button>
          )}
          {actions && !hideRetry && (
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                {/* Span wrapper so the tooltip still fires on a disabled button. */}
                <span tabIndex={canRetry ? -1 : 0}>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-error text-error hover:text-error cursor-pointer disabled:cursor-not-allowed"
                    onClick={onRetry}
                    isLoading={retryBusy}
                    data-testid="batch-funnel-verdict-retry-button"
                    disabled={!canRetry}
                  >
                    {!retryBusy && <RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    {t('batchFunnel.actions.retry')}
                  </Button>
                </span>
              </TooltipTrigger>
              {!canRetry && retryTooltip && (
                <TooltipContent
                  className="max-w-72"
                  data-testid="batch-funnel-verdict-retry-tooltip"
                >
                  {retryTooltip}
                </TooltipContent>
              )}
            </Tooltip>
          )}
        </div>
      </div>

      {(failure !== null || !!bannerError) && (
        <div
          className="border-error/30 bg-card space-y-1 rounded-lg border px-3 py-2 text-sm"
          data-testid="batch-funnel-verdict-failure-banner"
        >
          <p className="text-error flex flex-wrap items-center gap-1.5 font-medium">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('batchFunnel.failure.title', { stage: stageLabel(failureStage) })}
            {failureCode && (
              <code
                className="bg-error-50 rounded px-1.5 py-0.5 text-xs"
                data-testid="batch-funnel-verdict-failure-code-badge"
              >
                {failureCode}
              </code>
            )}
            {bannerError && (
              <StageErrorMeta
                affectedCount={bannerError.affectedCount}
                occurrences={bannerError.occurrences}
                lastOccurredAt={bannerError.lastOccurredAt}
              />
            )}
          </p>
          {failureMessage && (
            <p
              className="text-foreground break-words"
              data-testid="batch-funnel-verdict-failure-message"
            >
              {failureMessage}
            </p>
          )}
          {failureNextStep && (
            <p className="text-sm">
              <span className="text-foreground font-medium">{t('batchFunnel.whatToDo')}</span>{' '}
              <span className={cn(hideRetry ? 'text-foreground' : 'text-muted-foreground')}>
                {failureNextStep}
              </span>
            </p>
          )}
          <p className="text-muted-foreground text-xs">
            {failure?.failedAt &&
              t('batchFunnel.failure.at', { at: formatDateTime(failure.failedAt) })}
            {hideRetry && ` · ${t('batchFunnel.failure.notRetryable')}`}
          </p>
          {/* Raw technical cause stays behind the expander, never in the headline (§5.6). */}
          <StageErrorDetails
            rootCause={failure?.rootCause ?? bannerError?.rootCause}
            lastOccurredAt={bannerError?.lastOccurredAt}
            runId={bannerError?.runId}
            testId="batch-funnel-verdict-failure-details"
          />
        </div>
      )}

      {gate && (
        <div
          className="bg-card space-y-1 rounded-lg border px-3 py-2 text-sm"
          data-testid="batch-funnel-verdict-gate-banner"
        >
          <p
            className="text-foreground flex items-center gap-1.5 font-medium"
            data-testid="batch-funnel-verdict-gate-type"
          >
            <Hourglass className="text-warning h-4 w-4 shrink-0" aria-hidden="true" />
            {t(`batchFunnel.gate.type.${gate.type ?? 'UNKNOWN'}`, {
              defaultValue: gate.type ?? '—',
            })}
          </p>
          <dl className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
            {gate.waitingSince && (
              <div>
                <dt className="inline">{t('batchFunnel.gate.waitingSince')} </dt>
                <dd className="text-foreground inline">{formatDateTime(gate.waitingSince)}</dd>
              </div>
            )}
            {gate.remindAt && (
              <div>
                <dt className="inline">{t('batchFunnel.gate.remindAt')} </dt>
                <dd className="text-foreground inline">{formatDateTime(gate.remindAt)}</dd>
              </div>
            )}
            {gate.expiresAt && (
              <div>
                <dt className="inline">{t('batchFunnel.gate.expiresAt')} </dt>
                <dd className="text-foreground inline">{formatDateTime(gate.expiresAt)}</dd>
              </div>
            )}
          </dl>
          {gate.onNoDecision && (
            <p className="text-muted-foreground text-xs">{gate.onNoDecision}</p>
          )}
        </div>
      )}

      {stalled && (
        <p
          className="text-warning flex items-center gap-1.5 text-sm"
          data-testid="batch-funnel-verdict-stalled-banner"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('batchFunnel.health.stalled', {
            duration: formatDurationMs(health.stalledForMillis),
          })}
          {health.blockedReason && (
            <span className="text-muted-foreground">· {health.blockedReason}</span>
          )}
        </p>
      )}
    </section>
  );
}
